import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { LinkButton, PrimaryButton } from '../../../components/buttons';
import { inputClass, inputInvalidClass } from '../../../components/form-styles';
import { Field } from '../../../components/forms';
import { Page } from '../../../components/page';
import { Spinner } from '../../../components/spinner';

import { AlertContext } from '../../../contexts/alert-context';
import { isDemoStore } from '../../../data/store';
import { useStores } from '../../../hooks/stores';
import { storeLayout } from '../../../router';
import { type FormValues, validationSchema } from './form-data';
import { LabelsForm } from './labels-form';
import { UsersForm } from './users-form';

function Edit() {
  const { store } = storeLayout.useRouteContext();
  const { addErrorAlert, addSuccessAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const { updateStore } = useStores();

  // functions to build form returned by useForm() hook
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    formState,
    control,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...store,
      users: store.users || [],
      labels: [...store.labels].sort((a, b) => a.label.localeCompare(b.label)),
    },
  });

  // Ensures the form is reset when the store changes
  useEffect(() => {
    reset({
      ...store,
      users: store.users || [],
      labels: [...store.labels].sort((a, b) => a.label.localeCompare(b.label)),
    });
  }, [store, reset]);

  const onSubmit = useCallback(
    (data: FormValues) => {
      const updatedStore = {
        ...store,
        ...data,
        users: data.users || [],
      };
      updateStore(updatedStore)
        .then(() => {
          addSuccessAlert('Store updated', { keepAfterRouteChange: true });
          navigate({ to: '/stores/$storeId', params: { storeId: updatedStore.id } });
        })
        .catch((error) =>
          addErrorAlert(error.message || 'Failed to update store', { cause: error })
        );
    },
    [updateStore, store, navigate, addSuccessAlert, addErrorAlert]
  );

  return (
    <Page title="Edit Store">
      <form
        onSubmit={handleSubmit(onSubmit)}
        onReset={() => reset()}
        className="flex flex-col gap-4"
      >
        <div className="flex gap-10 items-start">
          <div className="flex flex-col gap-4">
            <Field label="Owner Name" name="owner_name">
              <input
                value={`${store.owner_first_name} ${store.owner_last_name}`}
                className={inputClass}
                disabled
              />
            </Field>
            <Field label="Store Name" name="name" errorMessage={errors.name?.message}>
              <input
                {...register('name')}
                placeholder="Mary's Liquor"
                className={errors.name ? inputInvalidClass : inputClass}
              />
            </Field>
          </div>
          <Field label="Labels" name="labels">
            <LabelsForm control={control} errors={errors} register={register} />
          </Field>
        </div>
        {!isDemoStore(store) && (
          <div className="flex flex-col gap-2">
            <Field label="Authorized Users" name="users">
              <UsersForm control={control} errors={errors} register={register} store={store} />
            </Field>
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <PrimaryButton type="submit" disabled={formState.isSubmitting}>
            {formState.isSubmitting && <Spinner />}
            Update
          </PrimaryButton>
          <LinkButton to="/stores/$storeId" params={{ storeId: store.id }}>
            Cancel
          </LinkButton>
        </div>
      </form>
    </Page>
  );
}

export { Edit };
